// src/hooks/useCalibratedPhysicsWaveform.ts
import { useMemo, useCallback, useRef, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getCalibrationForSession, CalibrationResult } from '../services/telemetry/calibrationApi';
import { DEBUG } from '../config/debug';
import { p95, roundUpToNice } from '../utils/powerCal';
import { useErrorTracking } from './useErrorTracking';

type InputPoint = {
  time: number;
  [rowerKey: string]: number; // e.g., 'Rower 1': power_value
};

type CalibratedPoint = {
  time: number;
  [rowerKey: string]: number;
};

type Output = {
  alpha: number;
  method: string | null;
  calibratedData: CalibratedPoint[];
  domainMax: number;
  ready: boolean;
  hasCalibration: boolean;
  refetchCalibration: () => Promise<void>;
};

export function useCalibratedPhysicsWaveform(sessionId: string, rawData: InputPoint[]): Output {
  // Avoid spamming console when rawData is temporarily empty during load
  const noDataLoggedRef = useRef(false);
  const queryClient = useQueryClient();

  // Initialize Sentry error tracking
  const { reportError, trackInteraction } = useErrorTracking('useCalibratedPhysicsWaveform');

  // Use React Query for calibration fetching (caching, background updates)
  const {
    data: calibration,
    isLoading: calibrationLoading,
    error: calibrationError,
  } = useQuery<CalibrationResult | null>({
    queryKey: ['calibration', sessionId],
    queryFn: async () => {
      if (DEBUG.CALIBRATION || DEBUG.ALL) {
        // eslint-disable-next-line no-console
        console.log('[CALIBRATION_DEBUG] Fetching calibration for session:', sessionId);
      }
      const result = await getCalibrationForSession(sessionId);
      if (DEBUG.CALIBRATION || DEBUG.ALL) {
        // eslint-disable-next-line no-console
        console.log('[CALIBRATION_DEBUG] Calibration fetched:', {
          sessionId,
          alpha: result?.alpha,
          method: result?.method,
          hasCalibration: result ? 'yes' : 'no',
        });
      }
      return result;
    },
    enabled: !!sessionId,
    staleTime: 10 * 60 * 1000, // 10 minutes (calibration rarely changes)
    gcTime: 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 1,
  });

  // Handle calibration errors
  useEffect(() => {
    if (calibrationError) {
      console.error('Failed to fetch calibration:', calibrationError);
      reportError(
        calibrationError instanceof Error ? calibrationError : new Error(String(calibrationError)),
        {
          context: 'calibration_fetch_error',
          sessionId,
          hookName: 'useCalibratedPhysicsWaveform',
        }
      );
    }
  }, [calibrationError, sessionId, reportError]);

  const refetchCalibration = useCallback(async () => {
    trackInteraction('calibration_refetch', {
      sessionId,
      hookName: 'useCalibratedPhysicsWaveform',
    });
    await queryClient.invalidateQueries({ queryKey: ['calibration', sessionId] });
  }, [sessionId, trackInteraction, queryClient]);

  const ready = !calibrationLoading;

  const { calibratedData, domainMax, alpha, method, hasCalibration } = useMemo(() => {
    if (!rawData || rawData.length === 0) {
      if (!noDataLoggedRef.current && (DEBUG.CALIBRATION || DEBUG.ALL)) {
        // eslint-disable-next-line no-console
        console.log('[CALIBRATION_DEBUG] No raw data to calibrate');
        noDataLoggedRef.current = true; // log once per mount/refetch cycle
      }
      return {
        calibratedData: [],
        domainMax: 500,
        alpha: 1,
        method: null,
        hasCalibration: false,
      };
    } else {
      // Reset for future cycles once data is present
      noDataLoggedRef.current = false;
    }

    // Log input data sample
    if (DEBUG.CALIBRATION || DEBUG.ALL) {
      // eslint-disable-next-line no-console
      console.log('[CALIBRATION_DEBUG] Input raw data sample:', {
        totalPoints: rawData.length,
        firstPoint: rawData[0],
        secondPoint: rawData[1],
        thirdPoint: rawData[2],
      });
    }

    const alpha =
      calibration?.alpha && Number.isFinite(calibration.alpha) && calibration.alpha > 0
        ? calibration.alpha
        : 1;

    if (DEBUG.CALIBRATION || DEBUG.ALL) {
      // eslint-disable-next-line no-console
      console.log('[CALIBRATION_DEBUG] Alpha calculation:', {
        calibrationAlpha: calibration?.alpha,
        isFinite: Number.isFinite(calibration?.alpha),
        isPositive: (calibration?.alpha as any) > 0,
        finalAlpha: alpha,
        defaultedTo1: alpha === 1,
      });
    }

    const method = calibration?.method || null;
    const hasCalibration = alpha !== 1 && method !== null;

    // Get rower keys (exclude 'time')
    const rowerKeys = Object.keys(rawData[0]).filter((key) => key !== 'time');

    // Apply calibration to each rower's data
    const calibratedData = rawData.map((point, index) => {
      const calibrated: CalibratedPoint = { time: point.time };

      rowerKeys.forEach((rowerKey, rkIndex) => {
        const rawValue = point[rowerKey];
        if (typeof rawValue === 'number' && Number.isFinite(rawValue)) {
          try {
            calibrated[rowerKey] = rawValue * alpha;

            // Log first few calculations for debugging
            // Reduce log noise: only log first point of first rower
            if ((DEBUG.CALIBRATION || DEBUG.ALL) && index === 0 && rkIndex === 0) {
              // eslint-disable-next-line no-console
              console.log(`[CALIBRATION_DEBUG] Point ${index} scaling for ${rowerKey}:`, {
                rawValue,
                alpha,
                scaledValue: rawValue * alpha,
                calculation: `${rawValue} * ${alpha} = ${rawValue * alpha}`,
              });
            }
          } catch (calcError) {
            // Report calibration calculation errors to Sentry
            reportError(calcError instanceof Error ? calcError : new Error(String(calcError)), {
              context: 'calibration_calculation_error',
              sessionId,
              rowerKey,
              pointIndex: index,
              rawValue,
              alpha,
            });
            calibrated[rowerKey] = rawValue; // Fallback to original value
          }
        } else {
          calibrated[rowerKey] = rawValue;
        }
      });

      return calibrated;
    });

    // Log output data sample
    if (DEBUG.CALIBRATION || DEBUG.ALL) {
      // eslint-disable-next-line no-console
      console.log('[CALIBRATION_DEBUG] Output calibrated data sample:', {
        totalPoints: calibratedData.length,
        firstPoint: calibratedData[0],
        secondPoint: calibratedData[1],
        thirdPoint: calibratedData[2],
        alphaUsed: alpha,
        hasCalibration,
      });
    }

    // Calculate 95th percentile for domain from all calibrated values
    const allCalibratedValues: number[] = [];
    calibratedData.forEach((point) => {
      rowerKeys.forEach((key) => {
        const value = point[key];
        if (typeof value === 'number' && Number.isFinite(value)) {
          allCalibratedValues.push(value);
        }
      });
    });

    const p95Value = p95(allCalibratedValues);
    const domainMax = roundUpToNice(p95Value);

    return {
      calibratedData,
      domainMax,
      alpha,
      method,
      hasCalibration,
    };
  }, [rawData, calibration, sessionId, reportError]);

  return {
    alpha,
    method,
    calibratedData,
    domainMax,
    ready,
    hasCalibration,
    refetchCalibration,
  };
}
